// O mesmo `authFetch` do painel atual (engine/src/ui/app.js:60-74), com duas
// diferenças que valem ser ditas:
//
//  - o erro de HTTP passa a virar exceção. No painel antigo `api()` fazia
//    `.json()` sem olhar `r.ok`, então um 500 chegava na tela como `undefined`
//    em vez de erro — era a origem das telas em branco que o `withState`
//    tentava cobrir. Aqui o corpo `{error}` vira `ErroDaApi`, e quem trata é o
//    TanStack Query;
//  - o "deslogar e mostrar o login" não acontece aqui. A função avisa por
//    exceção e quem decide é o provedor de autenticação; assim este arquivo não
//    precisa conhecer roteador nem estado de tela.
//
// O que NÃO muda: uma única tentativa de refresh por requisição, o refresh
// falhando implica esquecer as credenciais, e o header `content-type` vai em
// toda chamada (a API atual responde por ele até em GET).
import {
  esquecerCredenciais, guardarCredenciais, lerCredenciais, type Credenciais,
} from './tokens';

export class ErroDaApi extends Error {
  constructor(readonly status: number, mensagem: string, readonly corpo?: unknown) {
    super(mensagem);
    this.name = 'ErroDaApi';
  }
}

/** Sessão morta: o provedor de autenticação escuta isto para voltar ao login. */
export class SessaoExpirada extends Error {
  constructor() {
    super('sessão expirada');
    this.name = 'SessaoExpirada';
  }
}

type Ouvinte = () => void;
const ouvintesDeExpiracao = new Set<Ouvinte>();

/** Avisa a aplicação de que o refresh falhou; devolve o cancelamento. */
export function aoExpirar(f: Ouvinte): () => void {
  ouvintesDeExpiracao.add(f);
  return () => ouvintesDeExpiracao.delete(f);
}

function expirou(): void {
  esquecerCredenciais();
  for (const f of ouvintesDeExpiracao) f();
}

async function renovar(refresh: string): Promise<Credenciais | null> {
  const r = await fetch('/api/auth/refresh', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refresh }),
  });
  if (!r.ok) return null;
  return (await r.json()) as Credenciais;
}

async function comAutenticacao(caminho: string, init: RequestInit = {}): Promise<Response> {
  let cred = lerCredenciais();
  if (!cred) { expirou(); throw new SessaoExpirada(); }

  const enviar = (c: Credenciais) => fetch(caminho, {
    ...init,
    headers: { ...(init.headers || {}), authorization: `Bearer ${c.token}`, 'content-type': 'application/json' },
  });

  let r = await enviar(cred);
  if (r.status !== 401) return r;

  const nova = await renovar(cred.refresh);
  if (!nova) { expirou(); throw new SessaoExpirada(); }
  guardarCredenciais(nova);
  cred = nova;
  r = await enviar(cred);
  // 401 depois de renovar não é sessão vencida, é permissão: o token novo é
  // válido e o servidor ainda recusou. Deslogar aqui esconderia o motivo.
  return r;
}

async function corpoDe<T>(r: Response): Promise<T> {
  const texto = await r.text();
  let corpo: unknown = null;
  if (texto) {
    try { corpo = JSON.parse(texto); } catch { corpo = texto; }
  }
  if (r.ok) return corpo as T;
  const msg = (corpo && typeof corpo === 'object' && 'error' in corpo)
    ? String((corpo as { error: unknown }).error)
    : `HTTP ${r.status}`;
  throw new ErroDaApi(r.status, msg, corpo);
}

export const pegar = async <T>(caminho: string): Promise<T> => corpoDe<T>(await comAutenticacao(caminho));

export const postar = async <T>(caminho: string, corpo?: unknown): Promise<T> =>
  corpoDe<T>(await comAutenticacao(caminho, { method: 'POST', body: JSON.stringify(corpo ?? {}) }));

export const remendar = async <T>(caminho: string, corpo?: unknown): Promise<T> =>
  corpoDe<T>(await comAutenticacao(caminho, { method: 'PATCH', body: JSON.stringify(corpo ?? {}) }));

export const apagar = async <T>(caminho: string): Promise<T> =>
  corpoDe<T>(await comAutenticacao(caminho, { method: 'DELETE' }));

/** Login não passa pelo interceptador: é ele que produz o token. */
export async function entrar(email: string, password: string): Promise<Credenciais> {
  const r = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  return corpoDe<Credenciais>(r);
}
